import { useEffect, useSyncExternalStore } from 'react';
import { useAppSelector } from '@/app/hooks';
import api from '@/services/api';
import { ApiResponse, AppNotification, PagedResponse } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants';
import { onPush } from './push';

/**
 * Unread badge count. No count endpoint exists: derived from page 0 like the Android app.
 * Module-level store so Header and the inbox page share one number.
 */
let unread = 0;
let loadedForShop: number | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export const setUnreadCount = (n: number) => { unread = n; emit(); };

export async function refreshUnread(shopId: number) {
    try {
        const res = await api.get<ApiResponse<PagedResponse<AppNotification>>>(API_ENDPOINTS.NOTIFICATIONS(shopId), {
            params: { page: 0, size: 20, sort: 'createdAt,desc' },
        });
        loadedForShop = shopId;
        setUnreadCount((res.data.data.content || []).filter((n) => !n.isRead).length);
    } catch { /* badge is best-effort */ }
}

const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };

export function useUnreadNotifications(): number {
    const shopId = useAppSelector((s) => s.auth.selectedShop?.shopId);
    useEffect(() => {
        if (!shopId) return;
        if (loadedForShop !== shopId) refreshUnread(shopId);
        return onPush(() => refreshUnread(shopId));
    }, [shopId]);
    return useSyncExternalStore(subscribe, () => unread, () => 0);
}
